import clsx from "clsx";
import {
  Badge,
  type BadgeTone,
  Icon,
  Tag,
  Tooltip,
} from "@bfc-software/pallet";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";

import type { TaskStatus } from "../-data/types";
import type { PlanStatusKey } from "../-data/plans-model";

/**
 * The small repeated pieces of a task row: progress, tags, status, identity.
 *
 * Held together in one file rather than one per component because none of them
 * stands alone - they only ever appear inside a row or a card, and splitting them
 * would spread five files across two consumers to no benefit.
 */

/**
 * How each on-screen status reads as a tone.
 *
 * Exhaustive over `TaskStatus`, so a status added to the union fails to
 * compile here rather than rendering as an untoned tag nobody notices. The
 * union is exactly the activation listing's four statuses, and every task
 * `TaskStatusTag` is asked to draw a tone for is a released one - a planned
 * task has no `status` field at all (`Task`'s `planned` arm), so it never
 * reaches this component; it draws `NotReleasedMark` instead. That is still
 * true tab-wide for the All tasks tab, which reads the activation listing
 * exclusively - see the `TaskStatus` docblock in `types.ts`.
 */
const STATUS_TONES: Record<TaskStatus, BadgeTone> = {
  // SR-342 item 3.1. `unassigned` and `assigned` are both "Open, purple": the
  // PM's Status column answers "has anyone started this?", and who is holding
  // it is the Selector column's question - which is why item 10 (blank rather
  // than "Unassigned" there) ships with this and not after it.
  unassigned: "purple",
  assigned: "purple",
  claimed: "warning",
  completed: "success",
};

/**
 * The i18n key under `tasks.status` for each status.
 *
 * Not `Record<TaskStatus, TaskStatus>` any more: `unassigned` and `assigned`
 * collapse onto one label (see `STATUS_TONES`), so the map is doing real work
 * rather than restating the union.
 */
const STATUS_LABEL_KEYS: Record<TaskStatus, string> = {
  unassigned: "open",
  assigned: "open",
  claimed: "claimed",
  completed: "completed",
};

const PLAN_STATUS_TONES: Record<PlanStatusKey, BadgeTone> = {
  notActivated: "neutral",
  // An activation that failed needs the manager's eye, not a neutral tag that
  // reads the same as a plan nobody has touched yet.
  activationFailed: "danger",
  activating: "info",
  // SR-342 item 3.2: the PM asked for "ACTIVATED, blue". `info` is turquoise;
  // `accent` is the blue container pair.
  activated: "accent",
  completed: "success",
};

export function TaskStatusTag({ status }: { status: TaskStatus }) {
  const [t] = useCurrentLocalization();
  return (
    // The wrapper stays: `data-status` is the row's own hook and carries the
    // status the badge no longer distinguishes by label (unassigned vs
    // assigned), and the e2e specs key off both attributes.
    <span data-test-role="task-status" data-status={status}>
      {/* SR-342 items 2/3: a pill, which is what `Badge` is. Sentence case -
          the PM named these "Open", "In progress", "Complete"; only plan
          status is all caps. */}
      <Badge
        label={t(`tasks.status.${STATUS_LABEL_KEYS[status]}`)}
        color={{ mode: "static", tone: STATUS_TONES[status] }}
      />
    </span>
  );
}

/**
 * A task nobody has released, in the Status column.
 *
 * An icon rather than a `Tag`, and deliberately not a fifth `TaskStatus`. Every
 * chip in this column is a claim about the floor, and "nothing has been asked
 * of this task yet" is a statement about the plan above it - giving it chip
 * shape would put it in the same vocabulary as `Unassigned` and invite it to be
 * read as a status a selector could act on. Not silent either: the icon is
 * focusable through pallet's `Tooltip`, which carries the explanation, and a
 * visually hidden label names it for a screen reader since a tooltip is only a
 * description. Mirrors `PickStatusCell`'s `unknown` arm in
 * `task-detail-drawer.tsx`, and deliberately does not look like it - `unknown`
 * means "we could not find out" and reads as danger; this means "nothing has
 * happened yet" and must not be mistaken for something having gone wrong.
 */
export function NotReleasedMark() {
  const [t] = useCurrentLocalization();
  return (
    <span data-test-role="task-status" data-status="not-released">
      <Tooltip content={t("tasks.row.notReleasedTooltip")}>
        <Icon name="MinusCircle" size={16} />
        <span className="sr-only">{t("tasks.row.notReleased")}</span>
      </Tooltip>
    </span>
  );
}

export function PlanStatusTag({ statusKey }: { statusKey: PlanStatusKey }) {
  const [t] = useCurrentLocalization();
  return (
    <span data-test-role="plan-status" data-status={statusKey}>
      {/* SR-342 item 2: a pill, and all caps - the one place the PM asked for
          upper case, which is why `textCase` is a prop on `Badge` rather than
          a rule in `badge.module.css`. */}
      <Badge
        label={t(`tasks.planStatus.${statusKey}`)}
        color={{ mode: "static", tone: PLAN_STATUS_TONES[statusKey] }}
        textCase="upper"
      />
    </span>
  );
}

/**
 * Picks done against picks total.
 *
 * The bar and the count always come from the same two numbers - a bar without
 * the count cannot distinguish 1/2 from 500/1000, and the count without the bar
 * cannot be scanned down a column.
 */
export function Progress({
  done,
  total,
  percent,
  wide = false,
  label,
}: {
  done: number;
  total: number;
  percent: number;
  /** The card's meter fills its column; a row's is a fixed width. */
  wide?: boolean;
  /** Pre-formatted `n/n picks`. Omitted in tables, which show `n/n`. */
  label?: string;
}) {
  return (
    <div
      className={clsx("flex items-center", wide && "w-full")}
      data-test-role="progress"
    >
      {/* No width/height utility names an 82x7 track, so the fixed track
          sits as an inline style; a `wide` bar drops the fixed width for
          `flex-1` instead, which is why the width half of the style is
          conditional rather than always set. */}
      <div
        className={clsx(
          "flex-none overflow-hidden rounded-4 bg-neutral-container",
          wide && "flex-1 w-auto min-w-0",
        )}
        // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
        style={wide ? { height: 7 } : { width: 82, height: 7 }}
      >
        <div
          className={clsx(
            "h-full rounded-4 bg-primary",
            percent === 100 && "bg-success",
          )}
          // No `transition-duration`/`transition-property` utility spells
          // 160ms on pallet's scale, so the whole transition stays a literal
          // alongside the bar's own dynamic width.
          // eslint-disable-next-line react/forbid-dom-props -- runtime-computed value
          style={{ width: `${percent}%`, transition: "width 160ms ease-out" }}
        />
      </div>
      <span className="ml-8 whitespace-nowrap text-on-surface-variant text-size-12">
        {label ?? `${done}/${total}`}
      </span>
    </div>
  );
}

/**
 * A short list of warehouse labels - currently a plan's route codes.
 *
 * Auto-toned, and named after what it does rather than after its caller: the
 * labels come from the warehouse, so there is no fixed set to assign tones to,
 * and hashing the label at least keeps a given one the same colour everywhere it
 * appears.
 */
export function AutoTags({ labels }: { labels: ReadonlyArray<string> }) {
  return (
    <div className="flex">
      {labels.map((label, index) => (
        <div key={label} className={clsx("flex-none", index > 0 && "ml-4")}>
          <Tag label={label} display={{ tone: "auto" }} />
        </div>
      ))}
    </div>
  );
}

/** The task's identity: the number said out loud, over the routes it serves. */
export function TaskIdentity({
  taskId,
  routeCodes,
}: {
  taskId: string;
  /**
   * Every route the task picks from. A merged plan's task spans several
   * (ADR-0013), and listing them is the honest answer - naming one of them would
   * be picking a winner, and naming none would hide that the task serves any.
   */
  routeCodes: ReadonlyArray<string>;
}) {
  const [t] = useCurrentLocalization();
  return (
    <div className="flex flex-col">
      <span className="font-mono text-size-13 font-heading text-on-surface">
        {t("tasks.row.task", { id: taskId })}
      </span>
      <span className="mt-4 text-on-surface-muted text-size-12">
        {routeCodes.length > 0
          ? t("tasks.row.route", { id: routeCodes.join(", ") })
          : t("tasks.row.none")}
      </span>
    </div>
  );
}
